/* Captions on a picture: what they are made of, and where the lines break.
 *
 * Here rather than in the component for the reason lib/watermark.js and
 * lib/overlay.js are: tests/test_palette.py forbids a raw hex in a .vue so the
 * interface can follow the theme, and these are the opposite of that. White
 * caption text has to stay white on a light page, because it is drawn onto the
 * visitor's picture rather than painted onto ours.
 *
 * WHY THE LAYOUT LIVES HERE AND NOT IN EITHER RENDERER.
 *
 * A caption is drawn TWICE. imageTransform.js draws it on a canvas to make the
 * bytes; WatermarkLayer.vue draws it as CSS text over the stage so that typing
 * shows up without re-encoding a fifteen-megapixel photograph on every
 * keystroke -- App.vue makes that argument at length, and it is the right one.
 *
 * A watermark survives being written twice because it is one line at one size:
 * two implementations of "put this string in that corner" agree by accident.
 * A caption does not. It wraps, and it shrinks to fit, and two hand-agreed
 * wrapping algorithms will disagree on some sentence nobody thought to try --
 * at which point the preview lies about the file. tests/preview_css_test.mjs
 * exists because that class of divergence has already shipped once here.
 *
 * So neither renderer decides. layoutCaption() decides, once, and both ask it.
 *
 * THE TRICK THAT MAKES ONE ANSWER SERVE TWO SIZES: nothing in here is in
 * pixels. Lengths are fractions of the picture's SHORT edge and text is
 * measured per unit of font size, so the answer for a 3000px photograph and
 * the answer for the 480px preview of that same photograph are the same
 * numbers. Each renderer multiplies by the short edge it actually has. There
 * is no rounding to disagree about and no second decision to get wrong.
 *
 * WHAT IS STILL NOT IDENTICAL, because it is worth being exact rather than
 * comfortable. One decision is shared; the INPUTS to it are two measurements
 * of the same picture and they are not bit-identical. `aspect` reaches the
 * canvas as plan.dw/plan.dh -- the source shape after the iOS size cap has
 * rounded both edges to whole pixels -- and reaches the preview as the decoded
 * width over the decoded height. Those differ by at most about one part in
 * four thousand. A line whose width lands inside that fraction of the wrap
 * limit could therefore break in one place on screen and the other in the
 * file. It is a far smaller gap than the one that existed before the preview
 * was given the true shape (it was handed a box rounded to whole CSS pixels,
 * good only to about one part in five hundred), and closing it completely
 * would mean the canvas laying text out against a shape it is not drawing
 * into. So: one answer, two very slightly different questions, and no
 * pretending otherwise.
 */

/* White first, and by a long way: the convention this is imitating is white
 * letters with a black outline, which is legible on more pictures than any
 * other pairing. The rest are there because a caption is sometimes about
 * matching something in the photograph rather than shouting over it.
 *
 * Keys shared with WATERMARK_COLOURS wherever one already fits, so the
 * catalogue's existing names are reused -- "navy" IS marine blue, and there was
 * no reason to translate a second word for it.
 *
 * EVERY HEX HERE IS CHOSEN AGAINST memeOutline(), which switches the keyline
 * between black and white at a Rec. 709 luma of 140. Measured, so that no
 * colour sits on the fence where a small change to the hex would silently flip
 * the outline: white +115, yellow +68, lightBlue +42, orange +21 all take a
 * black keyline; green -17, rose -57, brown -59, navy -99, black -140 all take
 * a white one. Two of those are within about twenty of the line, which is
 * deliberate but worth knowing before nudging one. */
export const MEME_COLOURS = [
	{key: "white", hex: "#ffffff"},
	{key: "black", hex: "#000000"},
	{key: "yellow", hex: "#ffd60a"},
	{key: "orange", hex: "#ff9500"},
	{key: "rose", hex: "#b3365e"},
	{key: "green", hex: "#1f9d3a"},
	{key: "lightBlue", hex: "#56ccf2"},
	{key: "navy", hex: "#1b2a4a"},
	{key: "brown", hex: "#7a4a21"},
]

export const DEFAULT_MEME_COLOUR = "#ffffff"

/* The faces, in the order they are offered.
 *
 * Anton is first and is the default because the face everybody pictures --
 * Impact -- IS NOT AVAILABLE on most of the devices that will visit. It ships
 * with Windows and macOS and not with Android, iOS or most Linux, where
 * 'Impact, Haettenschweiler, "Arial Narrow Bold"' falls all the way through to
 * a plain sans and the caption quietly stops looking like a caption. Anton is
 * a webfont, so it is the same on every device, which is the entire point.
 *
 * `weight` is per-face rather than the flat 600 in watermark.js's fontSpec():
 * Anton ships one weight, 400, and is already heavier than most 900s. Asking
 * for 600 would make the browser synthesise a fake bold on top of a face that
 * does not need one.
 *
 * Impact is kept as a second option for the people who do have it, and Lato
 * Black as a third that is loaded anyway. Both stacks end in a generic family,
 * because the drawing happens on the visitor's device and a missing face has
 * to fall back rather than fail.
 */
export const MEME_FONTS = [
	{key: "anton", name: "Anton", weight: 400, webfont: true,
		stack: 'Anton, Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif'},
	{key: "bebas", name: "Bebas Neue", weight: 400, webfont: true,
		stack: '"Bebas Neue", Impact, "Arial Narrow Bold", sans-serif'},
	{key: "archivo", name: "Archivo Black", weight: 400, webfont: true,
		stack: '"Archivo Black", Impact, "Arial Black", sans-serif'},
	{key: "luckiest", name: "Luckiest Guy", weight: 400, webfont: true,
		stack: '"Luckiest Guy", "Comic Sans MS", cursive'},
	{key: "spaceMono", name: "Space Mono", weight: 700, webfont: true,
		stack: '"Space Mono", "Courier New", Courier, monospace'},
	{key: "courier", name: "Courier", weight: 700, webfont: false,
		stack: '"Courier New", Courier, "DejaVu Sans Mono", monospace'},
	{key: "impact", name: "Impact", weight: 400, webfont: false,
		stack: 'Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif'},
	{key: "latoBlack", name: "Lato Black", weight: 900, webfont: true,
		stack: 'Lato, "Noto Sans", sans-serif'},
	{key: "georgia", name: "Georgia", weight: 700, webfont: false,
		stack: 'Georgia, "Times New Roman", Times, serif'},
]

export const MEME_FONT_KEYS = MEME_FONTS.map((f) => f.key)
export const DEFAULT_MEME_FONT = "anton"

function faceFor(key) {
	return MEME_FONTS.find((f) => f.key === key) || MEME_FONTS[0]
}

export function memeFontStack(key) {
	return faceFor(key).stack
}

export function memeFontWeight(key) {
	return faceFor(key).weight
}

/* One spelling of the CSS font shorthand, shared by the canvas, the preview and
 * the font-loading wait, so none of the three can ask for a different face. */
export function memeFontSpec(px, key) {
	const face = faceFor(key)
	return `${face.weight} ${px}px ${face.stack}`
}

/* Two captions, named rather than numbered, because the UI has two fields and
 * "top" and "bottom" are what they are called everywhere else. */
export const MEME_SLOTS = ["top", "bottom", "left", "right"]

/* The slots actually on offer, in order.
 *
 * One door for the question "is this zone live?", asked by the rectangles, the
 * stamps, the panel and the bake. Four places deciding it separately is how a
 * caption comes to be drawn in a rectangle the panel will not let you edit.
 *
 * Clamped rather than trusted: a stored value from an older session, or a
 * picture declaring more areas than this build knows, must not produce a
 * slot that has no field, no box and no renderer behind it.
 */
export function memeSlots(zones) {
    const n = Math.max(1, Math.min(MEME_SLOTS.length, Number(zones) || 1))
    return MEME_SLOTS.slice(0, n)
}

/* The store fields behind one slot.
 *
 * The convention is the slot name capitalised -- memeTop, memeTopColor,
 * memeTopSize, memeTopBox -- and it is written down HERE, once, because four
 * slots times four fields is sixteen names and the alternative is sixteen
 * chances to typo one into a field that silently writes nowhere.
 */
export function memeStoreKeys(slot) {
	const cap = slot.charAt(0).toUpperCase() + slot.slice(1)
	return {
		text: `meme${cap}`,
		color: `meme${cap}Color`,
		size: `meme${cap}Size`,
		box: `meme${cap}Box`,
	}
}

/* The shape usableMeme() takes, read off the store.
 *
 * ONE builder, because there are two callers -- the download pass in App.vue
 * and the clip converter in file-list.js -- and they were two hand-written
 * copies of the same object literal. With two slots that was a duplication;
 * with four it is a standing invitation for one of them to forget an area, and
 * the symptom would be a caption that previews and does not bake.
 *
 * A CLOSED AREA CONTRIBUTES NO WORDS. The text stays in the store, so closing
 * an area and opening it again brings it back, but nothing that is not on the
 * picture may reach the bytes.
 */
export function memePayload(store, zones) {
	const live = memeSlots(zones)
	const out = {font: store.memeFont, upper: !!store.memeUpper}
	for (const slot of MEME_SLOTS) {
		const k = memeStoreKeys(slot)
		out[slot] = live.includes(slot) ? (store[k.text] || "") : ""
		out[`${slot}Color`] = store[k.color]
		out[`${slot}Size`] = store[k.size]
		out[`${slot}Box`] = store[k.box] || null
	}
	return out
}

/* Long enough for a sentence in each half, short enough that a pasted essay
 * cannot be rendered at the floor size and take the canvas with it. */
export const MAX_MEME_CHARS = 160

/* As a fraction of the SHORT edge, like everything else drawn onto a picture
 * here, so one setting reads the same on a portrait and a landscape and on a
 * thumbnail and a six-thousand-pixel photograph.
 *
 * The default is much larger than a watermark's 0.06: a watermark is meant to
 * be ignorable and a caption is the point of the picture. */
export const MIN_MEME_SIZE = 0.05
export const MAX_MEME_SIZE = 0.22
export const DEFAULT_MEME_SIZE = 0.12

/* The floor the shrink-to-fit will not go below. Past this the words are too
 * small to read anyway, so overflowing is the better failure -- and hardWrap()
 * below makes sure "overflowing" still means "inside the picture". */
export const MEME_FLOOR_SIZE = 0.035

/* Clear of the edge, as a fraction of the short edge. Enough that a caption
 * does not touch the frame and small enough not to waste a wide picture. */
export const MEME_MARGIN = 0.035

/* Tight, the way the convention is. 1.08 leaves the descenders room without
 * opening the block up into something that reads as a paragraph. */
export const MEME_LINE_HEIGHT = 1.08

/* Four lines is already a lot of words on a picture. Beyond it the caption is
 * competing with the image rather than captioning it.
 *
 * The limit is looser inside a rectangle the visitor drew, because there the
 * height they chose IS the constraint and they chose it on purpose. Refusing a
 * fifth line in a box plainly tall enough for eight would be the tool arguing
 * with somebody who has already said what they want. */
export const MEME_MAX_LINES = 4
export const MEME_MAX_LINES_IN_BOX = 10

/* No single caption may eat more than this share of the height.
 *
 * It was 0.40, which was defensible while the bands were invisible -- two of
 * them plus the margins left a fifth of the picture clear, and nothing was
 * drawn to show it. Once both rectangles are ON the picture from the start
 * that number reads quite differently: two dashed boxes 40% tall meeting in
 * the middle look like one region split in half rather than two caption zones,
 * which is not what they are.
 *
 * 0.32 leaves a clear gap of about three tenths of the picture between them and
 * still holds two lines at the default size without shrinking anything. */
export const MEME_MAX_BLOCK = 0.32

/* And the height of a LABEL box -- the two areas that open in the middle.
 *
 * Smaller than a band on purpose: it has to fit in the gap the top and bottom
 * bands leave, which on a square picture is 0.29 of the short edge, and a
 * label is a word or two on a subject rather than a sentence across the frame.
 */
export const MEME_LABEL_BLOCK = 0.2

/* Each step down in the shrink-to-fit. Small enough that the result does not
 * jump, large enough that the loop ends quickly. */
const SHRINK_STEP = 0.94

/* The size the measurer works at. Text advance is linear in font size, so
 * measuring once at a reference size and dividing gives a per-unit width that
 * is valid at every size -- which is what lets the whole of this file be
 * scale-free. */
export const MEASURE_PX = 100

/* Tidy the text the same way for both renderers.
 *
 * Uppercase is applied HERE and not to the value in the store, so the field
 * keeps showing what the visitor typed while the picture shows the convention.
 * Turning the toggle off has to give them their own capitals back.
 *
 * OFF by default. Shouting is the convention but it is not always the joke, and
 * a tool that silently rewrites what somebody typed is the wrong way round --
 * the checkbox is right there, and a caption in the case it was typed in is
 * never a surprise.
 */
export function normaliseCaption(text, upper = false) {
	if (typeof text !== "string") return ""
	const tidy = text.replace(/\s+/g, " ").trim().slice(0, MAX_MEME_CHARS)
	return upper ? tidy.toLocaleUpperCase() : tidy
}

/* A per-unit text measurer, built the same way by both renderers.
 *
 * Exported rather than left to the callers because "set the font, measure,
 * divide by the reference" is exactly the kind of three-line ritual that two
 * call sites eventually perform differently. The cache is not an optimisation
 * detail: the shrink loop measures the same handful of strings once per step.
 */
export function captionMeasurer(ctx, fontKey) {
	const spec = memeFontSpec(MEASURE_PX, fontKey)
	const seen = new Map()
	return (str) => {
		let width = seen.get(str)
		if (width === undefined) {
			// Re-asserted on every fresh measurement rather than once when the
			// measurer is built. The canvas renderer sets ctx.font to the
			// DRAWING size between one caption and the next, and a measurer
			// that quietly inherited that would report widths at the wrong
			// scale -- producing a layout that disagrees with the preview,
			// which is the single failure this module exists to prevent.
			ctx.font = spec
			width = ctx.measureText(str).width / MEASURE_PX
			seen.set(str, width)
		}
		return width
	}
}

/* The outline colour, black under light text and white under dark, so the
 * words survive whatever they land on. Rec. 709 luma, which is more than
 * enough for a decision with two outcomes.
 *
 * SOLID, unlike the watermark's, which is the reason this is not that function.
 * A watermark's 45%-alpha outline is meant to be unobtrusive; at caption size
 * the same thing reads as grey mush around every letter. The convention being
 * imitated is a hard black keyline.
 */
export function memeOutline(hex) {
	const n = parseInt(String(hex || "").slice(1), 16)
	if (!Number.isFinite(n)) return "#000000"
	const luma = 0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)
	return luma > 140 ? "#000000" : "#ffffff"
}

/* The keyline's width, as a fraction of the font size. Shared so that the
 * canvas stroke and the preview's text-shadow are the same thickness. */
export const MEME_OUTLINE_RATIO = 1 / 8

/* Greedy, which is what every reader expects: fill a line, then start another.
 * A word too wide for a line of its own still gets one -- the caller shrinks,
 * and hardWrap() catches whatever is left at the floor. */
function greedyWrap(words, size, maxWidth, measure) {
	const lines = []
	let line = ""
	for (const word of words) {
		const candidate = line ? line + " " + word : word
		if (!line || size * measure(candidate) <= maxWidth) {
			line = candidate
		} else {
			lines.push(line)
			line = word
		}
	}
	if (line) lines.push(line)
	return lines
}

/* The last resort, at the floor size: break wherever it has to, word
 * boundaries included, so that nothing runs off the side of the picture. A
 * pasted URL is the case this exists for. */
function hardWrap(text, size, maxWidth, measure) {
	const lines = []
	let line = ""
	for (const ch of text) {
		const candidate = line + ch
		if (line && size * measure(candidate) > maxWidth) {
			lines.push(line)
			line = ch
		} else {
			line = candidate
		}
	}
	if (line) lines.push(line)
	return lines
}

/* The smallest rectangle worth drawing into, as a fraction of the picture. A
 * stray click on the stage produces a sliver, and a sliver would be filled with
 * text at the floor size that nobody can read. Below this the box is discarded
 * in favour of the default, which is the same bargain lib/imageTransform.js
 * strikes with MIN_CROP_PX. */
export const MIN_MEME_BOX = 0.06

/* A caption box, validated. Stored as FRACTIONS of the picture's width and
 * height rather than pixels, so that it survives the picture being scaled, the
 * preview being a different size from the canvas, and the file being re-encoded
 * on the way out. Anything outside the picture, upside down or too small to
 * read in comes back null, which every caller reads as "use the default".
 */
export const MEME_ANCHORS = ["top", "bottom", "centre"]

export function usableMemeBox(box) {
	if (!box || typeof box !== "object") return null
	const {x, y, w, h} = box
	if (![x, y, w, h].every((n) => Number.isFinite(n))) return null
	if (w < MIN_MEME_BOX || h < MIN_MEME_BOX) return null
	// Clamped rather than refused: a rectangle dragged a pixel past the edge is
	// a rectangle at the edge, not a mistake.
	const left = Math.min(Math.max(x, 0), 1)
	const top = Math.min(Math.max(y, 0), 1)
	const width = Math.min(w, 1 - left)
	const height = Math.min(h, 1 - top)
	if (width < MIN_MEME_BOX || height < MIN_MEME_BOX) return null
	/* The anchor travels WITH the rectangle, and that is the point of it.
	 *
	 * Where the words sit inside their rectangle cannot be decided from the
	 * slot, because two rectangles in the same slot want different things. The
	 * default band is a big region at the top or the bottom of the picture, and
	 * words in it should hug the frame the way the format does. A white caption
	 * box declared with a library picture is drawn tight around the space the
	 * artwork left for it, and words in THAT should sit in the middle of it --
	 * yes-no's two boxes are side by side at the same height, so anchoring one
	 * to its top and the other to its bottom would offset a matched pair by
	 * most of their height.
	 *
	 * Carrying it on the rectangle is also what stops the words jumping when
	 * one is dragged: a rectangle keeps whatever it was created with, so moving
	 * it moves the words and nothing else.
	 */
	return {
		x: left, y: top, w: width, h: height,
		anchor: MEME_ANCHORS.includes(box.anchor) ? box.anchor : "top",
	}
}

/* The box in the units the rest of this file speaks: fractions of the SHORT
 * edge. Exported so that the canvas and the preview convert it the same way --
 * they hold the same fractions and differ only in which picture they are about
 * to multiply them by.
 */
export function boxInUnits(box, aspect) {
	const usable = usableMemeBox(box)
	if (!usable || !Number.isFinite(aspect) || aspect <= 0) return null
	const boxW = aspect >= 1 ? aspect : 1
	const boxH = aspect >= 1 ? 1 : 1 / aspect
	return {
		x: usable.x * boxW,
		y: usable.y * boxH,
		w: usable.w * boxW,
		h: usable.h * boxH,
		anchor: usable.anchor,
	}
}

/* The band a caption uses when nobody has drawn a rectangle for it.
 *
 * Exported because it is needed in two places that must agree exactly: here,
 * to fit the words, and CropSurface.vue, to put the handles somewhere. If the
 * handles started anywhere other than where the words are, picking a rectangle
 * up would move them before the visitor had done anything.
 *
 * Slot-aware, which the placement rule used to be instead. Since captionTops()
 * anchors to the rectangle's near edge, the bottom caption's band has to BE at
 * the bottom -- there is nowhere else for that knowledge to live now.
 */
export function defaultCaptionBox(slot, aspect) {
	const boxW = aspect >= 1 ? aspect : 1
	const boxH = aspect >= 1 ? 1 : 1 / aspect

	/* THE TWO LABEL AREAS OPEN IN THE MIDDLE, SIDE BY SIDE.
	 *
	 * A band the full width of the picture is right for a caption and wrong for
	 * a label. Four full-width bands cannot all fit, and the third would open
	 * exactly underneath the first -- one rectangle hidden behind another, with
	 * the handles of the wrong one under the pointer. These are half-width, in
	 * the gap the top and bottom bands leave, so a newly added area always
	 * arrives somewhere visible and empty. It is then dragged onto whatever it
	 * is labelling, which is what the three-label formats are for.
	 *
	 * Centred rather than hugging an edge: a label sits ON a subject, where a
	 * caption runs along the frame.
	 */
	if (slot === "left" || slot === "right") {
		const w = Math.max(MIN_MEME_BOX, (boxW - 3 * MEME_MARGIN) / 2)
		return {
			x: slot === "left" ? MEME_MARGIN : boxW - MEME_MARGIN - w,
			y: Math.max(0, (boxH - MEME_LABEL_BLOCK) / 2),
			w,
			h: Math.min(MEME_LABEL_BLOCK, boxH - 2 * MEME_MARGIN),
			anchor: "centre",
		}
	}

	const h = Math.max(0.1, Math.min(MEME_MAX_BLOCK, boxH - 2 * MEME_MARGIN))
	return {
		x: MEME_MARGIN,
		y: slot === "bottom" ? boxH - MEME_MARGIN - h : MEME_MARGIN,
		w: Math.max(0.1, boxW - 2 * MEME_MARGIN),
		h,
		// Hugging the frame, which is what the format looks like. Centring a
		// single line in a band a third of the picture tall would lift it away
		// from the edge for no reason anybody asked for.
		anchor: slot === "bottom" ? "bottom" : "top",
	}
}

/* Where the lines break, and how big they are. THE one answer both renderers use.
 *
 * Everything in and out is a fraction of the picture's SHORT edge:
 *
 *     aspect    the picture's width divided by its height. Not its size --
 *               a 4000x3000 photograph and its 480x360 preview are the same
 *               picture and must get the same answer.
 *     measure   from captionMeasurer(): the width of a string when the font
 *               size is 1.
 *
 * Returns null when there is nothing to draw, or {lines, size, lineHeight}
 * where `size` is the font size and `lineHeight` the distance between
 * baselines, both as fractions of the short edge.
 */
export function layoutCaption({text, aspect, measure, size = DEFAULT_MEME_SIZE,
                               upper = false, maxLines = null, box = null,
                               slot = "top"}) {
	const words = normaliseCaption(text, upper)
	if (!words) return null
	if (typeof measure !== "function") return null
	if (!Number.isFinite(aspect) || aspect <= 0) return null

	/* The rectangle the words have to fit inside, RESOLVED HERE and handed back
	 * on the result.
	 *
	 * Both renderers need it -- to know how wide a line may be, and to know
	 * what to centre the letters on -- and if each worked it out from the
	 * stored fractions there would be two answers again, which is the one
	 * thing this module exists to prevent. So it is resolved once, beside the
	 * line breaking that depends on it.
	 */
	const drawn = boxInUnits(box, aspect)
	const area = drawn || defaultCaptionBox(slot, aspect)
	const maxWidth = area.w
	const ceiling = area.h
	// A rectangle the visitor drew is a height they meant; the default one is a
	// guess at how much of a photograph a caption may eat.
	const lineCap = Number.isFinite(maxLines) && maxLines > 0
		? maxLines
		: (drawn ? MEME_MAX_LINES_IN_BOX : MEME_MAX_LINES)

	const asked = Math.min(MAX_MEME_SIZE, Math.max(MIN_MEME_SIZE,
		Number.isFinite(size) ? size : DEFAULT_MEME_SIZE))
	const parts = words.split(" ")

	// Shrink until it fits. Every step is the same arithmetic on both sides, so
	// both sides take the same number of them and stop at the same number.
	for (let px = asked; px >= MEME_FLOOR_SIZE; px *= SHRINK_STEP) {
		const lines = greedyWrap(parts, px, maxWidth, measure)
		const fits = lines.length <= lineCap
			&& lines.length * px * MEME_LINE_HEIGHT <= ceiling
			&& lines.every((line) => px * measure(line) <= maxWidth)
		if (fits) {
			return {lines, size: px, lineHeight: px * MEME_LINE_HEIGHT, box: area}
		}
	}

	// Past the floor. Break anywhere, and keep only the lines the picture has
	// room for.
	//
	// Written as a filter rather than dividing the ceiling by the line height
	// because NOTHING IN THIS FUNCTION MAY ROUND. Rounding is what would make
	// the 3000px canvas and the 480px preview take a different number of
	// shrink steps and break in different places, which is the one failure
	// this whole module is shaped to prevent -- so the rule is absolute and has
	// no exceptions to remember, not even a harmless floor() over a line count.
	// tests/meme_layout_test.mjs enforces it by reading this function.
	const px = MEME_FLOOR_SIZE
	const step = px * MEME_LINE_HEIGHT
	const broken = hardWrap(words, px, maxWidth, measure)
	const lines = broken.filter((_, i) => (i + 1) * step <= ceiling).slice(0, lineCap)
	return {lines: lines.length ? lines : broken.slice(0, 1), size: px,
		lineHeight: step, box: area}
}

/* Where each line sits, top edge first, in short-edge units from the top of the
 * picture. Exported so that the canvas and the stylesheet cannot each invent
 * their own idea of where "the bottom caption" starts.
 *
 * The top caption grows downwards from the top margin and the bottom one grows
 * upwards from the bottom margin, which is what makes two captions on one
 * picture look deliberate rather than merely placed.
 */
export function captionTops(slot, layout) {
	if (!layout || !layout.lines.length) return []
	const area = layout.box
	const block = layout.lines.length * layout.lineHeight
	/* One rule, read off the RECTANGLE rather than the slot.
	 *
	 * It was briefly keyed off the slot, and that could not survive a library
	 * picture declaring where its own caption boxes are: yes-no's two white
	 * boxes sit side by side at the same height, so "first slot to the top,
	 * second to the bottom" offset a matched pair by most of their height.
	 * Reading the anchor off the rectangle means the picture can say what it
	 * wants, the default band keeps hugging the frame, and a rectangle that is
	 * dragged keeps whatever it already had -- so nothing jumps.
	 */
	const first = area.anchor === "centre"
		? area.y + (area.h - block) / 2
		: (area.anchor === "bottom" ? area.y + area.h - block : area.y)
	return layout.lines.map((_, i) => first + i * layout.lineHeight)
}

/* What the letters are centred on, across. The default box is the width of the
 * picture less its margins, so this is the middle of the picture until somebody
 * draws a rectangle somewhere else. */
export function captionCentre(layout, aspect) {
	const boxW = aspect >= 1 ? aspect : 1
	if (!layout || !layout.box) return boxW / 2
	return layout.box.x + layout.box.w / 2
}
